import type { ReactNode } from 'react';

export type BlogCardData = {
  title: string;
  description: string;
  slug: string; // without the blog/ prefix
  category: string;
  author: string;
  publishDate: string;
  readTime: string;
  heroImage?: string | null;
};

export function Arrow() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 10h11M10.5 5l5 5-5 5" />
    </svg>
  );
}

export function CalIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="4.5" width="18" height="16" rx="3" /><path d="M8 2.5v4M16 2.5v4M3 10h18M9 15l2 2 4-4" /></svg>
  );
}

export function StarIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2.8l2.8 5.9 6.4.8-4.7 4.4 1.2 6.4L12 17.2 6.3 20.3l1.2-6.4L2.8 9.5l6.4-.8z" /></svg>
  );
}

/** Proof row under a closing band: the canonical figures only (CLAUDE.md social proof). */
export function ProofRow() {
  return (
    <ul className="bp-close-p" aria-label="Proof">
      <li><span className="bp-stars"><StarIcon /><StarIcon /><StarIcon /><StarIcon /><StarIcon /></span><b>4.9</b> stars</li>
      <li><b>750+</b> Google reviews</li>
      <li><b>5,247+</b> loan officers</li>
    </ul>
  );
}

/** Plain text of a React node tree (heading ids, TOC). */
export function textOf(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  const el = node as { props?: { children?: ReactNode } };
  return el.props ? textOf(el.props.children) : '';
}

export function slugify(s: string): string {
  return s.toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80);
}

/** Headings (## / ###) from MDX source, outside code fences, for the server-rendered first paint of the TOC (the client re-reads the real DOM). */
export function tocFromSource(src: string): { id: string; text: string; level: 2 | 3 }[] {
  const out: { id: string; text: string; level: 2 | 3 }[] = [];
  const seen = new Set<string>();
  let fence = false;
  for (const line of src.split('\n')) {
    if (/^\s*(```|~~~)/.test(line)) { fence = !fence; continue; }
    if (fence) continue;
    const m = /^(#{2,3})\s+(.+?)\s*#*\s*$/.exec(line);
    if (!m) continue;
    const text = m[2].replace(/\[([^\]]+)\]\([^)]*\)/g, '$1').replace(/[*_`]/g, '').replace(/<[^>]+>/g, '').trim();
    let id = slugify(text) || 'section';
    const base = id; let n = 2;
    while (seen.has(id)) id = `${base}-${n++}`;
    seen.add(id);
    out.push({ id, text, level: m[1].length === 2 ? 2 : 3 });
  }
  return out;
}
